import React, { useCallback, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import {
  CancellationEligibility,
  cancelOrder,
  getCancellationEligibility,
} from "../services/order";
import { KitchenOrder } from "../types";
import {
  BlockedModal,
  CancellationConfirmationModal,
  LoadingModal,
} from "../components";

const reasonMessages: Record<string, string> = {
  order_not_found: "Pedido não encontrado.",
  order_already_canceled: "Este pedido já foi cancelado.",
  order_outside_cancellation_window:
    "O pedido só pode ser cancelado em até 3 dias após a compra.",
};

const getErrorMessage = (error: any) =>
  error?.response?.data?.message
  || error?.message
  || "Não foi possível cancelar o pedido. Tente novamente.";

interface UseOrderCancellationOptions {
  onSuccess?: () => void | Promise<void>;
}

export const useOrderCancellation = ({
  onSuccess,
}: UseOrderCancellationOptions = {}) => {
  const queryClient = useQueryClient();
  const lock = useRef(false);
  const [order, setOrder] = useState<KitchenOrder | null>(null);
  const [eligibility, setEligibility] =
    useState<CancellationEligibility | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [error, setError] = useState("");

  const executeCancellation = useCallback(async (target: KitchenOrder) => {
    if (lock.current) return;
    lock.current = true;
    setIsCanceling(true);

    try {
      const items = target.items.map((item) => ({
        id: item.id,
        quantity: 0,
        price: item.price,
        name: item.name,
        totalAmount: item.totalAmount,
      }));
      await cancelOrder(target.id, items);
    } catch (cancellationError) {
      setError(getErrorMessage(cancellationError));
      lock.current = false;
      setIsCanceling(false);
      return;
    }

    try {
      setEligibility(null);
      setOrder(null);
      await Promise.all([
        queryClient.invalidateQueries("realTimeOrders"),
        queryClient.invalidateQueries("ordersScheduleOrder"),
        queryClient.invalidateQueries("allOrders"),
        queryClient.invalidateQueries("todayOrders"),
      ]);
      await onSuccess?.();
    } catch (refreshError) {
      console.error("Failed to refresh orders after cancellation", refreshError);
    } finally {
      lock.current = false;
      setIsCanceling(false);
    }
  }, [onSuccess, queryClient]);

  const requestCancellation = useCallback(async (target: KitchenOrder) => {
    if (lock.current) return;
    lock.current = true;
    setError("");
    setIsChecking(true);

    try {
      const result = await getCancellationEligibility(target.id);
      if (!result.eligible) {
        setError(
          reasonMessages[result.reason || ""]
          || "Este pedido não pode ser cancelado.",
        );
        return;
      }

      setOrder(target);
      setEligibility(result);
      if (!result.hasMultipleOrders) {
        lock.current = false;
        await executeCancellation(target);
      }
    } catch (eligibilityError) {
      setError(getErrorMessage(eligibilityError));
    } finally {
      lock.current = false;
      setIsChecking(false);
    }
  }, [executeCancellation]);

  const dismiss = () => {
    if (isCanceling) return;
    setEligibility(null);
    setOrder(null);
  };

  const confirmation = (
    <>
      <LoadingModal
        open={isChecking}
        message="Verificando se o pedido pode ser cancelado..."
      />
      <CancellationConfirmationModal
        open={!!eligibility?.hasMultipleOrders}
        orderCount={eligibility?.orderCount || 0}
        totalAmount={eligibility?.totalAmount || 0}
        isLoading={isCanceling}
        onDismiss={dismiss}
        onConfirm={() => {
          if (order) executeCancellation(order);
        }}
      />
      <BlockedModal
        open={!!error}
        message={error}
        onClose={() => setError("")}
      />
    </>
  );

  return {
    requestCancellation,
    confirmation,
    isLoading: isChecking || isCanceling,
  };
};
